// Lightweight synthesized sound effects via Web Audio API (no asset files).
let ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// Paper / ticket tearing sound. Built from granular "fiber snaps": sharp
// noise grains whose density accelerates across the tear, layered over a
// gritty noise bed and brightened with filtering so it clearly reads as a
// rip rather than a soft hiss. Must be triggered from a user gesture.
export function playTearSound() {
  try {
    const ac = getCtx();
    if (!ac) return;
    const sr = ac.sampleRate;
    const dur = 0.5;
    const len = Math.floor(sr * dur);
    const buffer = ac.createBuffer(1, len, sr);
    const data = buffer.getChannelData(0);

    // decay-per-sample so each fiber "snap" lasts ~4ms
    const grainDecay = Math.exp(-1 / (sr * 0.004));
    let grain = 0;

    for (let i = 0; i < len; i++) {
      const t = i / len; // 0..1
      // overall shape: fast attack, strong body, quick tail
      const env = Math.min(1, t * 10) * Math.pow(1 - t, 0.9);
      // probability of a new fiber snap grows as the tear accelerates
      const density = 0.004 + t * 0.03;
      if (Math.random() < density) grain = 1;
      grain *= grainDecay;
      const n = Math.random() * 2 - 1;
      // loud crackly grains + a quieter continuous rip bed
      const sample = n * (grain * 1.0 + 0.18) * env;
      data[i] = Math.max(-1, Math.min(1, sample * 1.4));
    }

    const src = ac.createBufferSource();
    src.buffer = buffer;

    // remove rumble, keep the crisp "paper" range
    const hp = ac.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 900;

    // resonant peak sweeping up = brightening rip
    const bp = ac.createBiquadFilter();
    bp.type = "peaking";
    bp.Q.value = 1.1;
    bp.gain.value = 9;
    bp.frequency.setValueAtTime(2200, ac.currentTime);
    bp.frequency.exponentialRampToValueAtTime(4800, ac.currentTime + dur);

    const gain = ac.createGain();
    gain.gain.setValueAtTime(0.0001, ac.currentTime);
    gain.gain.exponentialRampToValueAtTime(1.0, ac.currentTime + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + dur);

    src.connect(hp).connect(bp).connect(gain).connect(ac.destination);
    src.start();
    src.stop(ac.currentTime + dur + 0.02);

    // short haptic buzz reinforces the tear on supported devices
    if (navigator.vibrate) navigator.vibrate([18, 10, 28]);
  } catch (e) {
    // ignore: audio is a non-critical enhancement
  }
}
